import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import BlogNavigation from '../components/BlogNavigation';
import Footer from '../../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

const sections = [
  { id: 'what-is', label: 'What Is Google AI Studio?' },
  { id: 'prerequisites', label: 'Prerequisites' },
  { id: 'walkthrough', label: '6-Step Walkthrough' },
  { id: 'model-comparison', label: 'Model Comparison' },
  { id: 'rate-limits', label: 'Rate Limits' },
  { id: 'privacy', label: 'Privacy for Job Seekers' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'security', label: 'Security Best Practices' },
  { id: 'upgrade', label: 'When to Upgrade' },
];

type CalloutType = 'tip' | 'warning' | 'info' | 'danger';

function Callout({ type, title, children }: { type: CalloutType; title: string; children: React.ReactNode }) {
  const styles: Record<CalloutType, { bg: string; border: string; icon: string; iconColor: string; titleColor: string }> = {
    tip: {
      bg: 'bg-emerald-500/5 dark:bg-emerald-500/10',
      border: 'border-emerald-500/30',
      icon: 'ri-lightbulb-line',
      iconColor: 'text-emerald-400',
      titleColor: 'text-emerald-400',
    },
    warning: {
      bg: 'bg-amber-500/5 dark:bg-amber-500/10',
      border: 'border-amber-500/30',
      icon: 'ri-alert-line',
      iconColor: 'text-amber-400',
      titleColor: 'text-amber-400',
    },
    info: {
      bg: 'bg-[#6366F1]/5 dark:bg-[#6366F1]/10',
      border: 'border-[#6366F1]/30',
      icon: 'ri-information-line',
      iconColor: 'text-[#818CF8]',
      titleColor: 'text-[#818CF8]',
    },
    danger: {
      bg: 'bg-red-500/5 dark:bg-red-500/10',
      border: 'border-red-500/30',
      icon: 'ri-error-warning-line',
      iconColor: 'text-red-400',
      titleColor: 'text-red-400',
    },
  };
  const s = styles[type];
  return (
    <div className={`rounded-xl border ${s.bg} ${s.border} p-5 my-6`}>
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${s.iconColor}`}>
          <i className={`${s.icon} text-lg`}></i>
        </div>
        <div>
          <div className={`text-sm font-bold uppercase tracking-wide mb-1 ${s.titleColor}`}>{title}</div>
          <div className="text-gray-600 dark:text-[#94A3B8] text-sm leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ code, language = 'python' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative my-6 rounded-xl overflow-hidden border border-gray-200 dark:border-[#2D3148]">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-[#141828] border-b border-gray-200 dark:border-[#2D3148]">
        <span className="text-xs font-mono text-gray-400 dark:text-[#64748B]">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-[#64748B] hover:text-[#6366F1] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line' : 'ri-clipboard-line'}></i>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 bg-gray-50 dark:bg-[#0D1117] overflow-x-auto">
        <code className="text-sm font-mono text-gray-800 dark:text-[#E2E8F0] leading-relaxed whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

function SectionHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mt-14 mb-5 scroll-mt-28 flex items-center gap-3">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-8 mb-3">{children}</h3>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-[#94A3B8] text-base leading-relaxed mb-4">{children}</p>;
}

function BulletList({ items }: { items: (string | React.ReactNode)[] }) {
  return (
    <ul className="space-y-2 mb-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-1 flex-shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-[#6366F1]"></div>
          </div>
          <span className="text-gray-600 dark:text-[#94A3B8] text-base leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function CheckItem({ children, isGood }: { children: React.ReactNode; isGood: boolean }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${isGood ? 'text-emerald-400' : 'text-red-400'}`}>
        <i className={isGood ? 'ri-check-line text-base' : 'ri-close-line text-base'}></i>
      </div>
      <span className="text-gray-600 dark:text-[#94A3B8] text-sm leading-relaxed">{children}</span>
    </div>
  );
}

export default function GoogleAIStudioTutorial() {
  const [activeSection, setActiveSection] = useState('what-is');
  const [readingProgress, setReadingProgress] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [linkedinMsgCopied, setLinkedinMsgCopied] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);

  // SEO + Schema.org
  useEffect(() => {
    document.title = 'Get a Free Gemini API Key in Under 2 Minutes — Google AI Studio Tutorial | JobOS';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Step-by-step guide to getting a free Gemini API key from Google AI Studio. No credit card, no subscription — just your Google account. 1,500 free requests/day.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Get a Free Gemini API Key — Google AI Studio Tutorial | JobOS', true);
    setMeta('og:description', 'No credit card. No subscription. Just a Google account — unlock AI-powered resume tailoring inside JobOS completely free.', true);
    setMeta('og:url', `${SITE_URL}/blog/google-ai-studio-free-api-key`, true);
    setMeta('og:type', 'article', true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/4a52e3606b71bc99ccc199fe7b586ec6.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:image:alt', 'Get a Free Gemini API Key from Google AI Studio — No credit card, 1,500 requests/day free | JobOS', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Get a Free Gemini API Key in Under 2 Minutes — Google AI Studio | JobOS');
    setMeta('twitter:description', 'No credit card. No subscription. Just a Google account — unlock AI-powered resume tailoring inside JobOS completely free.');
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20Google%20Gemini%20AI%20Studio%20concept%20with%20glowing%20multicolored%20gemstone%20facets%20radiating%20teal%20and%20coral%20light%20beams%20in%20deep%20dark%20space%2C%20vibrant%20crystalline%20geometric%20shapes%20forming%20an%20API%20key%20symbol%2C%20futuristic%20developer%20tool%20aesthetic%2C%20clean%20minimal%20tech%20art%20representing%20Google%20AI%20intelligence%2C%20no%20text%2C%20high%20quality%20digital%20illustration%2C%20dark%20navy%20background%20with%20colorful%20accent%20lights&width=1200&height=630&seq=google-ai-studio-og-v1&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/google-ai-studio-free-api-key`);

    document.querySelectorAll('script[data-schema-page="google-ai-studio"]').forEach(el => el.remove());

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Get a Free Gemini API Key in Under 2 Minutes — Google AI Studio',
        description: 'Step-by-step guide to getting a free Gemini API key from Google AI Studio. No credit card required. 1,500 free requests/day.',
        url: `${SITE_URL}/blog/google-ai-studio-free-api-key`,
        datePublished: '2026-03-01',
        dateModified: '2026-03-01',
        author: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: 'JobOS',
          url: SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/og-image.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/google-ai-studio-free-api-key` },
        keywords: 'Google AI Studio, Gemini API key, free Gemini API, Google AI tutorial, free AI API key, Gemini 2.0 Flash',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Get a Free Gemini API Key from Google AI Studio',
        description: 'Get a free Gemini API key in under 2 minutes with just a Google account.',
        totalTime: 'PT2M',
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Go to Google AI Studio', text: 'Open aistudio.google.com in your browser.', url: 'https://aistudio.google.com' },
          { '@type': 'HowToStep', position: 2, name: 'Sign in with your Google Account', text: 'Use your existing Gmail account — no new account needed.' },
          { '@type': 'HowToStep', position: 3, name: 'Click "Get API key" in the sidebar', text: 'Navigate to aistudio.google.com/app/apikey.', url: 'https://aistudio.google.com/app/apikey' },
          { '@type': 'HowToStep', position: 4, name: 'Click "Create API Key"', text: 'Select or create a Google Cloud project and generate your key.' },
          { '@type': 'HowToStep', position: 5, name: 'Copy your API key', text: 'Copy the key immediately — it is only shown once.' },
          { '@type': 'HowToStep', position: 6, name: 'Paste into JobOS Settings', text: 'Go to JobOS → Settings → AI Configuration and paste your key.' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: 'Google AI Studio Free API Key', item: `${SITE_URL}/blog/google-ai-studio-free-api-key` },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'google-ai-studio');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="google-ai-studio"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;
      const { top, height } = article.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrolled = Math.max(0, -top);
      const total = height - windowHeight;
      const progress = total > 0 ? Math.min(100, (scrolled / total) * 100) : 0;
      setReadingProgress(progress);

      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140) {
            setActiveSection(sections[i].id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setTocOpen(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A]">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-gray-200 dark:bg-[#1E2538]">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] transition-all duration-100"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <BlogNavigation />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="mb-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-gray-400 dark:text-[#64748B] hover:text-[#6366F1] text-sm transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-[#64748B] mb-5">
            <span>Tutorials</span>
            <i className="ri-arrow-right-s-line"></i>
            <span>Free AI API Keys</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#818CF8]">Google AI Studio</span>
          </div>

          {/* Series badge */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#818CF8] text-xs font-semibold rounded-full">
              <i className="ri-book-open-line"></i> Tutorial Series
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full">
              <i className="ri-price-tag-3-line"></i> Free Tier
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold rounded-full">
              <i className="ri-time-line"></i> 2 min setup
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] text-gray-500 dark:text-[#94A3B8] text-xs font-semibold rounded-full">
              Part 1 of 3
            </span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white mb-5 leading-tight max-w-[860px]">
            Get a <span className="bg-gradient-to-r from-[#818CF8] to-[#6366F1] bg-clip-text text-transparent">Free</span> Gemini API Key<br />
            in Under 2 Minutes
          </h1>
          <p className="text-gray-500 dark:text-[#94A3B8] text-xl leading-relaxed max-w-[700px] mb-8">
            No credit card. No subscription. Just a Google account you already have — and you unlock AI-powered resume tailoring, cover letter writing, and job tracking inside JobOS completely free.
          </p>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-gray-200 dark:border-[#2D3148]">
            <div className="flex items-center gap-3">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20confident%20young%20man%20with%20dark%20hair%2C%20wearing%20a%20dark%20hoodie%2C%20clean%20minimal%20background%2C%20developer%20aesthetic%2C%20soft%20studio%20lighting%2C%20high%20quality%20portrait%20photography&width=80&height=80&seq=author-jobos-dev-v1&orientation=squarish"
                alt="JobOS Team"
                className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
              />
              <div>
                <div className="text-gray-900 dark:text-white text-sm font-semibold">JobOS Team</div>
                <div className="text-gray-400 dark:text-[#64748B] text-xs">jobos.dev</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 dark:text-[#64748B] text-sm">
              <i className="ri-quill-pen-line text-base"></i>
              <span className="font-medium text-gray-700 dark:text-gray-300">Published:</span>
              <time dateTime="2026-03-01" className="font-mono">March 1, 2026</time>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 dark:text-[#64748B] text-sm">
              <i className="ri-time-line text-base"></i>
              <span>12 min read</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#818CF8] text-xs font-bold">
                <i className="ri-refresh-line text-xs"></i>
                Last updated: March 2026
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                <i className="ri-shield-check-line text-xs"></i>
                Rate limits verified
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Cover Image */}
      <section className="pb-10 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="w-full h-[380px] rounded-2xl overflow-hidden border border-gray-200 dark:border-[#2D3148] relative">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20digital%20illustration%20of%20Google%20AI%20Studio%20interface%20concept%20with%20glowing%20neural%20network%20nodes%20and%20API%20key%20symbols%20floating%20in%20dark%20space%2C%20deep%20navy%20and%20indigo%20gradient%20background%20with%20emerald%20green%20accent%20lights%2C%20futuristic%20developer%20tool%20aesthetic%2C%20clean%20minimal%20tech%20art%2C%20no%20text%2C%20high%20quality%20digital%20illustration&width=1280&height=380&seq=google-ai-studio-hero-v1&orientation=landscape"
              alt="Google AI Studio Tutorial"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E1A]/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-8 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                <i className="ri-google-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-white font-bold text-sm">Google AI Studio</div>
                <div className="text-white/60 text-xs">aistudio.google.com</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Layout: Sidebar + Content */}
      <section className="pb-20 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="flex gap-10 items-start">

            {/* Sticky Sidebar */}
            <aside className="hidden xl:block w-72 flex-shrink-0 sticky top-28">
              {/* TL;DR */}
              <div className="rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117] p-5 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center text-[#818CF8]">
                    <i className="ri-flashlight-line text-base"></i>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#818CF8]">TL;DR</span>
                </div>
                <ul className="space-y-2">
                  {[
                    'Go to aistudio.google.com',
                    'Sign in with Gmail',
                    'Click "Get API key" in sidebar',
                    'Click "Create API key"',
                    'Copy & paste into JobOS',
                    'Done — 250 free requests/day',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-4 h-4 flex-shrink-0 rounded-full bg-[#6366F1]/20 text-[#818CF8] text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <span className="text-gray-600 dark:text-[#94A3B8] text-xs leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Table of Contents */}
              <div className="rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117] p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 flex items-center justify-center text-gray-400 dark:text-[#64748B]">
                    <i className="ri-list-unordered text-base"></i>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-[#64748B]">Contents</span>
                </div>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className={`w-full text-left text-xs px-3 py-2 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis ${
                        activeSection === s.id
                          ? 'bg-[#6366F1]/10 text-[#818CF8] font-semibold'
                          : 'text-gray-500 dark:text-[#64748B] hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#141828]'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Series nav */}
              <div className="rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117] p-5 mt-5">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-[#64748B] mb-3">Tutorial Series</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/20">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-[#818CF8] text-xs"></i>
                    </div>
                    <span className="text-[#818CF8] text-xs font-semibold">1. Google AI Studio</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg opacity-50">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-line text-gray-400 dark:text-[#64748B] text-xs"></i>
                    </div>
                    <span className="text-gray-400 dark:text-[#64748B] text-xs">2. OpenRouter (coming soon)</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg opacity-50">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-line text-gray-400 dark:text-[#64748B] text-xs"></i>
                    </div>
                    <span className="text-gray-400 dark:text-[#64748B] text-xs">3. Grok (coming soon)</span>
                  </div>
                </div>
              </div>

              {/* Tutorial Series */}
              <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-6 text-white">
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <i className="ri-book-open-line text-blue-400"></i>
                  Tutorial Series
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-500/20 border border-blue-400/30">
                    <i className="ri-google-fill text-blue-400"></i>
                    <span className="font-medium">Google AI Studio</span>
                  </div>
                  <Link 
                    to="/blog/groq-free-api-key"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <i className="ri-flashlight-fill text-orange-400"></i>
                    <span>Groq API</span>
                  </Link>
                  <Link 
                    to="/blog/grok-free-api-key"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <i className="ri-twitter-x-fill text-purple-400"></i>
                    <span>Grok (xAI)</span>
                  </Link>
                  <Link 
                    to="/blog/openrouter-free-api-key"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <i className="ri-route-line text-amber-400"></i>
                    <span>OpenRouter</span>
                  </Link>
                </div>
              </div>
            </aside>

            {/* Mobile TOC toggle */}
            <div className="xl:hidden fixed bottom-6 right-6 z-40">
              <button
                onClick={() => setTocOpen(!tocOpen)}
                className="w-12 h-12 flex items-center justify-center bg-[#6366F1] text-white rounded-full shadow-lg shadow-[#6366F1]/30 cursor-pointer"
              >
                <i className={tocOpen ? 'ri-close-line text-xl' : 'ri-list-unordered text-xl'}></i>
              </button>
              {tocOpen && (
                <div className="absolute bottom-14 right-0 w-64 rounded-xl border border-gray-200 dark:border-[#2D3148] bg-white dark:bg-[#0D1117] shadow-2xl p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-[#64748B] mb-3">Contents</div>
                  <nav className="space-y-1">
                    {sections.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => scrollTo(s.id)}
                        className={`w-full text-left text-xs px-3 py-2 rounded-lg transition-all cursor-pointer ${
                          activeSection === s.id
                            ? 'bg-[#6366F1]/10 text-[#818CF8] font-semibold'
                            : 'text-gray-500 dark:text-[#64748B] hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
            </div>

            {/* Article Content */}
            <article ref={articleRef} className="flex-1 min-w-0">

              {/* ── Section 1: What Is Google AI Studio ── */}
              <SectionHeading id="what-is">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 text-[#818CF8] flex-shrink-0">
                  <i className="ri-google-line text-lg"></i>
                </span>
                What Is Google AI Studio?
              </SectionHeading>

              <Prose>
                Google AI Studio is Google's free, browser-based playground for their Gemini AI models. Think of it as a control panel — while most people chat with Gemini directly at gemini.google.com, Google AI Studio lets you access Gemini programmatically as an API, so tools like JobOS can use it automatically on your behalf.
              </Prose>

              <Prose>
                The best part: Google offers a genuinely free tier — not a trial, not a credit. As long as you stay within daily limits (which are more than enough for active job searching), it costs you absolutely nothing, and it never expires.
              </Prose>

              <Callout type="tip" title="Why is Google giving this away free?">
                Google wants developers to build on Gemini. The free tier is their way of getting people hooked — and it works. For job seekers, it means you get full AI-powered resume tailoring, cover letter writing, and job tracking without spending a cent.
              </Callout>

              <SubHeading>What You Can Do With It</SubHeading>
              <BulletList items={[
                'Generate tailored resume bullet points matched to any job description',
                'Write personalized cover letters in seconds',
                'Get ATS keyword analysis and match scoring',
                'Auto-fill job applications with your profile data',
                'Ask follow-up questions about any job posting',
                'Summarize long job descriptions into key requirements',
              ]} />

              <Callout type="info" title="JobOS + Google AI Studio">
                JobOS uses your Gemini API key to run all AI features on your behalf. Your key stays in your browser — it never touches our servers. You're always in control.
              </Callout>

              {/* ── Section 2: Prerequisites ── */}
              <SectionHeading id="prerequisites">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                </span>
                Prerequisites
              </SectionHeading>

              <Prose>
                Genuinely, the setup is minimal. You need exactly three things:
              </Prose>

              <div className="space-y-4 mb-6">
                {[
                  {
                    icon: 'ri-mail-line',
                    color: 'text-[#818CF8]',
                    bg: 'bg-[#6366F1]/10',
                    title: 'A Google Account (Gmail)',
                    desc: 'Any free Gmail account works. The same one you use for YouTube, Google Drive, or Google Docs works perfectly. You don\'t need a new one.',
                  },
                  {
                    icon: 'ri-global-line',
                    color: 'text-emerald-400',
                    bg: 'bg-emerald-500/10',
                    title: 'A Web Browser',
                    desc: 'Chrome, Firefox, Safari, Edge — anything modern works. No downloads, no installs, no extensions required.',
                  },
                  {
                    icon: 'ri-time-line',
                    color: 'text-amber-400',
                    bg: 'bg-amber-500/10',
                    title: '~2 Minutes of Your Time',
                    desc: 'Seriously. We timed it. Google has made this process extremely streamlined — no forms, no verification, no waiting.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-5 rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117]">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${item.bg} flex-shrink-0`}>
                      <i className={`${item.icon} text-xl ${item.color}`}></i>
                    </div>
                    <div>
                      <div className="text-gray-900 dark:text-white font-semibold text-sm mb-1">{item.title}</div>
                      <div className="text-gray-500 dark:text-[#94A3B8] text-sm leading-relaxed">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <Callout type="warning" title="Regional Availability Note">
                Google AI Studio is available in most countries, but there are some regional restrictions. If you're in the EU, you may need to use a VPN or wait for Google to expand access. Check the current list at <strong>ai.google.dev/available_regions</strong>. If you can't access it, our OpenRouter tutorial (Part 2) covers a great alternative that works globally.
              </Callout>

              {/* ── Section 3: 6-Step Walkthrough ── */}
              <SectionHeading id="walkthrough">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 flex-shrink-0">
                  <i className="ri-route-line text-lg"></i>
                </span>
                Step-by-Step: Getting Your Free API Key
              </SectionHeading>

              <Prose>
                Follow these steps exactly. The whole process takes about 70 seconds.
              </Prose>

              <div className="space-y-6 mb-8">
                {[
                  {
                    step: 1,
                    title: 'Go to Google AI Studio',
                    desc: 'Open your browser and navigate to the official Google AI Studio URL. Bookmark it — you\'ll come back here to manage your keys.',
                    action: 'aistudio.google.com',
                    actionIcon: 'ri-external-link-line',
                    actionHref: 'https://aistudio.google.com',
                    note: 'You\'ll see a landing page with a "Get started" button. If you\'re already signed into Google, it may skip straight to the dashboard.',
                  },
                  {
                    step: 2,
                    title: 'Sign In with Your Google Account',
                    desc: 'Click the "Sign in" button (top right). Use your existing Gmail account — no new account needed.',
                    action: null,
                    note: 'If you have multiple Google accounts, pick the one you use most. This is just for authentication — it doesn\'t affect your Gmail inbox.',
                  },
                  {
                    step: 3,
                    title: 'Click "Get API key" in the Left Sidebar',
                    desc: 'Once you\'re in the dashboard, look at the left sidebar. You\'ll see "Get API key" — click it. It\'s usually in the top section of the sidebar.',
                    action: 'aistudio.google.com/app/apikey',
                    actionIcon: 'ri-key-line',
                    actionHref: 'https://aistudio.google.com/app/apikey',
                    note: 'This takes you to the API keys management page where you can see all your existing keys (if any) and create new ones.',
                  },
                  {
                    step: 4,
                    title: 'Click "Create API Key"',
                    desc: 'On the API keys page, click the blue "Create API key" button at the top. Google will ask which project to associate it with.',
                    action: null,
                    note: 'If you don\'t have a Google Cloud project yet, select "Create API key in new project" — Google creates one automatically. You don\'t need to configure anything.',
                  },
                  {
                    step: 5,
                    title: 'Copy Your API Key',
                    desc: 'Your new API key will appear — it looks like: AIzaSy... followed by a long string of letters and numbers. Click the copy icon next to it.',
                    action: null,
                    note: 'This is the only time Google shows you the full key. Copy it immediately. If you lose it, you\'ll need to create a new one (the old one can be deleted).',
                  },
                  {
                    step: 6,
                    title: 'Paste Into JobOS Settings',
                    desc: 'Go to JobOS → Settings → AI Configuration. Paste your API key into the "Gemini API Key" field and click Save. All AI features activate instantly.',
                    action: null,
                    note: 'JobOS stores your key locally in your browser. It\'s never sent to our servers. You can revoke it from Google AI Studio at any time.',
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-5">
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="w-9 h-9 rounded-full bg-[#6366F1] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                        {item.step}
                      </div>
                      {item.step < 6 && <div className="w-px flex-1 bg-gray-200 dark:bg-[#2D3148] mt-2 min-h-[24px]"></div>}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="text-gray-900 dark:text-white font-bold text-base mb-2">{item.title}</div>
                      <div className="text-gray-600 dark:text-[#94A3B8] text-sm leading-relaxed mb-3">{item.desc}</div>
                      {item.action && (
                        <a
                          href={item.actionHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#818CF8] text-xs font-semibold rounded-lg hover:bg-[#6366F1]/20 transition-colors cursor-pointer whitespace-nowrap mb-3"
                        >
                          <i className={item.actionIcon}></i>
                          {item.action}
                        </a>
                      )}
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-100 dark:bg-[#0D1117] border border-gray-200 dark:border-[#2D3148]">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 text-gray-400 dark:text-[#64748B]">
                          <i className="ri-information-line text-sm"></i>
                        </div>
                        <span className="text-gray-500 dark:text-[#64748B] text-xs leading-relaxed">{item.note}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Callout type="tip" title="Pro tip: Name your key">
                When creating the key, give it a descriptive name like "JobOS - Personal". This makes it easy to identify and revoke later if needed, especially if you create keys for multiple tools.
              </Callout>

              {/* ── Section 4: Model Comparison ── */}
              <SectionHeading id="model-comparison">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 text-[#818CF8] flex-shrink-0">
                  <i className="ri-bar-chart-grouped-line text-lg"></i>
                </span>
                Model Comparison: Flash vs Pro vs Flash-Lite
              </SectionHeading>

              <Prose>
                Google offers three main Gemini models on the free tier. Here's what actually matters for job seekers using JobOS:
              </Prose>

              <div className="overflow-x-auto mb-6 rounded-xl border border-gray-200 dark:border-[#2D3148]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#0D1117] border-b border-gray-200 dark:border-[#2D3148]">
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Model</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Speed</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Quality</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Free RPM</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Best For</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Rec.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#2D3148] bg-white dark:bg-[#0A0E1A]">
                    {[
                      {
                        model: 'Gemini 2.0 Flash',
                        modelSub: 'gemini-2.0-flash',
                        speed: '⚡ Very Fast',
                        speedColor: 'text-emerald-400',
                        quality: '★★★★☆',
                        qualityColor: 'text-amber-400',
                        rpm: '15 RPM',
                        bestFor: 'Resume tailoring, cover letters, ATS scoring',
                        rec: true,
                        recLabel: 'Recommended',
                        highlight: true,
                      },
                      {
                        model: 'Gemini 1.5 Pro',
                        modelSub: 'gemini-1.5-pro',
                        speed: '🐢 Slower',
                        speedColor: 'text-amber-400',
                        quality: '★★★★★',
                        qualityColor: 'text-amber-400',
                        rpm: '2 RPM',
                        bestFor: 'Complex analysis, long documents',
                        rec: false,
                        recLabel: '',
                        highlight: false,
                      },
                      {
                        model: 'Gemini 2.0 Flash-Lite',
                        modelSub: 'gemini-2.0-flash-lite',
                        speed: '⚡⚡ Fastest',
                        speedColor: 'text-emerald-400',
                        quality: '★★★☆☆',
                        qualityColor: 'text-amber-400',
                        rpm: '30 RPM',
                        bestFor: 'Quick lookups, simple rewrites',
                        rec: false,
                        recLabel: '',
                        highlight: false,
                      },
                    ].map((row, i) => (
                      <tr key={i} className={row.highlight ? 'bg-[#6366F1]/5 dark:bg-[#6366F1]/5' : 'bg-white dark:bg-[#0A0E1A]'}>
                        <td className="px-5 py-4">
                          <div className="text-gray-900 dark:text-white font-semibold text-sm">{row.model}</div>
                          <div className="text-gray-400 dark:text-[#64748B] text-xs font-mono mt-0.5">{row.modelSub}</div>
                        </td>
                        <td className={`px-5 py-4 text-sm font-medium ${row.speedColor}`}>{row.speed}</td>
                        <td className={`px-5 py-4 text-sm ${row.qualityColor}`}>{row.quality}</td>
                        <td className="px-5 py-4 text-gray-600 dark:text-[#94A3B8] text-sm font-mono">{row.rpm}</td>
                        <td className="px-5 py-4 text-gray-500 dark:text-[#94A3B8] text-xs max-w-[180px]">{row.bestFor}</td>
                        <td className="px-5 py-4">
                          {row.rec && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full whitespace-nowrap">
                              <i className="ri-check-line text-xs"></i> {row.recLabel}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Callout type="tip" title="Our recommendation: Gemini 2.0 Flash">
                For 99% of job seekers, <strong>Gemini 2.0 Flash</strong> is the sweet spot. It's fast enough that you won't notice any delay, the output quality is excellent for resume and cover letter tasks, and the 15 RPM free limit is more than enough for a full day of job searching. JobOS defaults to this model automatically.
              </Callout>

              {/* ── Section 5: Rate Limits ── */}
              <SectionHeading id="rate-limits">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 flex-shrink-0">
                  <i className="ri-speed-line text-lg"></i>
                </span>
                Rate Limits (Updated March 2026)
              </SectionHeading>

              <Prose>
                These are the real numbers from Google's official documentation as of March 2026. The free tier is genuinely generous for personal use.
              </Prose>

              <div className="overflow-x-auto mb-6 rounded-xl border border-gray-200 dark:border-[#2D3148]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#0D1117] border-b border-gray-200 dark:border-[#2D3148]">
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Model</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">RPM</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">TPM</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">RPD</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Context Window</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#2D3148] bg-white dark:bg-[#0A0E1A]">
                    {[
                      { model: 'Gemini 2.0 Flash', rpm: '15', tpm: '1,000,000', rpd: '1,500', ctx: '1M tokens' },
                      { model: 'Gemini 2.0 Flash-Lite', rpm: '30', tpm: '1,000,000', rpd: '1,500', ctx: '1M tokens' },
                      { model: 'Gemini 1.5 Flash', rpm: '15', tpm: '1,000,000', rpd: '1,500', ctx: '1M tokens' },
                      { model: 'Gemini 1.5 Flash-8B', rpm: '15', tpm: '1,000,000', rpd: '1,500', ctx: '1M tokens' },
                      { model: 'Gemini 1.5 Pro', rpm: '2', tpm: '32,000', rpd: '50', ctx: '2M tokens' },
                      { model: 'Gemini Embedding', rpm: '5', tpm: '100,000', rpd: '100', ctx: '2,048 tokens' },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#0D1117] transition-colors">
                        <td className="px-5 py-3.5 text-gray-900 dark:text-white font-medium text-sm">{row.model}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] font-mono text-sm">{row.rpm}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] font-mono text-sm">{row.tpm}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] font-mono text-sm">{row.rpd}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] text-sm">{row.ctx}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'RPM', full: 'Requests Per Minute', desc: 'How many API calls you can make in 60 seconds' },
                  { label: 'TPM', full: 'Tokens Per Minute', desc: 'Total input + output tokens processed per minute' },
                  { label: 'RPD', full: 'Requests Per Day', desc: 'Hard daily cap — resets at midnight Pacific Time' },
                ].map((item) => (
                  <div key={item.label} className="p-4 rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117]">
                    <div className="text-[#818CF8] font-bold text-sm mb-1">{item.label} <span className="text-gray-400 dark:text-[#64748B] font-normal text-xs">= {item.full}</span></div>
                    <div className="text-gray-500 dark:text-[#94A3B8] text-xs leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>

              <Callout type="info" title="What does 1,500 requests/day actually mean?">
                A typical JobOS session — tailoring a resume, writing a cover letter, and scoring your ATS match — uses about 3–5 API calls. At 1,500 RPD, you could do that for <strong>300–500 job applications per day</strong> before hitting the limit. For most job seekers, you'll never come close to the daily cap.
              </Callout>

              {/* ── Section 6: Privacy ── */}
              <SectionHeading id="privacy">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500/10 text-red-400 flex-shrink-0">
                  <i className="ri-shield-user-line text-lg"></i>
                </span>
                Privacy for Job Seekers
              </SectionHeading>

              <Prose>
                This section is written specifically for job seekers — because your resume contains sensitive personal data (address, phone, employment history, salary expectations) and you deserve to know exactly what happens to it when it hits Google's servers.
              </Prose>

              <SubHeading>What Google Receives</SubHeading>
              <Prose>
                When JobOS sends a request to the Gemini API, Google receives the text content of that request — which may include your resume text, the job description, and any instructions. Google processes this on their servers to generate a response.
              </Prose>

              <SubHeading>The Training Data Caveat</SubHeading>
              <Callout type="warning" title="Important: Free tier data may be used for training">
                By default, Google may use your API requests on the <strong>free tier</strong> to improve their models. This means the resume text and job descriptions you send could potentially be used as training data. This is standard practice across most free AI APIs — but it's worth knowing.
              </Callout>

              <SubHeading>How to Opt Out of Training Data</SubHeading>
              <Prose>
                You have two options to prevent your data from being used for training:
              </Prose>

              <div className="space-y-4 mb-6">
                {[
                  {
                    option: 'Option A',
                    title: 'Disable data sharing in Google AI Studio',
                    steps: [
                      'Go to aistudio.google.com',
                      'Click your profile icon (top right)',
                      'Select "Settings"',
                      'Under "Data sharing", toggle off "Help improve AI models"',
                    ],
                    badge: 'Free',
                    badgeColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                  },
                  {
                    option: 'Option B',
                    title: 'Upgrade to a paid plan',
                    steps: [
                      'Paid API usage (Pay-as-you-go) is explicitly excluded from training data',
                      'Google\'s terms state: "We do not use your paid API data to train our models"',
                      'See Section 9 for pricing details',
                    ],
                    badge: 'Paid',
                    badgeColor: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
                  },
                ].map((item, i) => (
                  <div key={i} className="p-5 rounded-xl border border-gray-200 dark:border-[#2D3148] bg-gray-50 dark:bg-[#0D1117]">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-gray-900 dark:text-white font-bold text-sm">{item.option}: {item.title}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${item.badgeColor}`}>{item.badge}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {item.steps.map((step, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <div className="w-4 h-4 flex-shrink-0 rounded-full bg-[#6366F1]/20 text-[#818CF8] text-[10px] font-bold flex items-center justify-center mt-0.5">{j + 1}</div>
                          <span className="text-gray-500 dark:text-[#94A3B8] text-sm">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <SubHeading>What JobOS Does With Your Data</SubHeading>
              <BulletList items={[
                'Your API key is stored only in your browser\'s local storage — never on our servers',
                'JobOS does not log, store, or read the content of your AI requests',
                'Your resume data is sent directly from your browser to Google\'s API — it never passes through JobOS servers',
                'You can delete your API key from JobOS settings at any time',
              ]} />

              <Callout type="tip" title="Practical advice for sensitive data">
                If you're concerned about privacy, consider removing your home address and phone number from the resume text you paste into JobOS for AI processing. Use a placeholder like "[Address on file]" — the AI doesn't need your actual address to tailor your resume.
              </Callout>

              {/* ── Section 7: Troubleshooting ── */}
              <SectionHeading id="troubleshooting">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500/10 text-red-400 flex-shrink-0">
                  <i className="ri-bug-line text-lg"></i>
                </span>
                Troubleshooting the 3 Most Common Errors
              </SectionHeading>

              <Prose>
                These three errors account for over 90% of issues people encounter when setting up their Gemini API key.
              </Prose>

              <div className="space-y-6 mb-6">
                {[
                  {
                    code: '403',
                    title: 'Permission Denied / API Not Enabled',
                    color: 'border-red-500/30 bg-red-500/5',
                    codeColor: 'text-red-400 bg-red-500/10',
                    cause: 'The Generative Language API hasn\'t been enabled for your Google Cloud project.',
                    fixes: [
                      'Go to console.cloud.google.com',
                      'Select your project from the dropdown',
                      'Search for "Generative Language API" in the search bar',
                      'Click "Enable" — it takes about 30 seconds',
                      'Try your API key again',
                    ],
                    note: 'If you created your key via aistudio.google.com/app/apikey, this should be enabled automatically. If you created it via the Google Cloud Console directly, you may need to enable it manually.',
                  },
                  {
                    code: '429',
                    title: 'Too Many Requests / Rate Limit Exceeded',
                    color: 'border-amber-500/30 bg-amber-500/5',
                    codeColor: 'text-amber-400 bg-amber-500/10',
                    cause: 'You\'ve hit either the per-minute (RPM) or per-day (RPD) limit for your model.',
                    fixes: [
                      'Wait 60 seconds and try again (if it\'s an RPM issue)',
                      'Check if you\'ve hit the daily limit — it resets at midnight Pacific Time',
                      'Switch to Gemini 2.0 Flash-Lite temporarily (30 RPM vs 15 RPM)',
                      'If you hit RPD limits regularly, consider upgrading to pay-as-you-go',
                    ],
                    note: 'The 429 error response body will tell you whether it\'s an RPM or RPD limit. Look for "RATE_LIMIT_EXCEEDED" vs "DAILY_LIMIT_EXCEEDED" in the error message.',
                  },
                  {
                    code: 'REGION',
                    title: 'Service Not Available in Your Region',
                    color: 'border-[#6366F1]/30 bg-[#6366F1]/5',
                    codeColor: 'text-[#818CF8] bg-[#6366F1]/10',
                    cause: 'Google AI Studio and the Gemini API are not available in all countries, particularly some EU countries and a few others.',
                    fixes: [
                      'Check ai.google.dev/available_regions for the current list',
                      'If your country is listed as unsupported, use a VPN set to a supported region',
                      'Alternatively, use our OpenRouter tutorial (Part 2) — it works globally',
                      'EU users: Google is actively expanding — check back in a few months',
                    ],
                    note: 'This is the most frustrating error because it\'s not something you can fix on your end. OpenRouter (Part 2 of this series) is the best workaround — it provides access to Gemini and other models without regional restrictions.',
                  },
                  {
                    code: 'KEY',
                    title: 'Lost Your API Key',
                    color: 'border-gray-300/30 dark:border-[#2D3148] bg-gray-100/50 dark:bg-[#0D1117]',
                    codeColor: 'text-gray-500 dark:text-[#94A3B8] bg-gray-200 dark:bg-[#141828]',
                    cause: 'Google hides the full key after creation. If you didn\'t save it, you can\'t retrieve it.',
                    fixes: [
                      'No panic — go to aistudio.google.com/app/apikey',
                      'Delete the old key (click the trash icon next to it)',
                      'Click "Create API key" to generate a new one',
                      'Copy the new key immediately and save it somewhere safe',
                      'Paste the new key into JobOS AI Config to restore all features',
                    ],
                    note: 'Creating a new key takes about 30 seconds. Your old key is invalidated the moment you delete it — any tools using it will stop working until you update them with the new key.',
                  },
                ].map((item, i) => (
                  <div key={i} className={`rounded-xl border p-6 ${item.color}`}>
                    <div className="flex items-start gap-4 mb-4">
                      <span className={`px-3 py-1 rounded-lg text-sm font-bold font-mono ${item.codeColor}`}>
                        {item.code}
                      </span>
                      <div className="text-gray-900 dark:text-white font-bold text-base">{item.title}</div>
                    </div>
                    <div className="text-gray-500 dark:text-[#94A3B8] text-sm mb-4">
                      <strong className="text-gray-700 dark:text-[#CBD5E1]">Cause:</strong> {item.cause}
                    </div>
                    <div className="text-gray-700 dark:text-[#CBD5E1] text-sm font-semibold mb-2">Fix:</div>
                    <ol className="space-y-1.5 mb-4">
                      {item.fixes.map((fix, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="w-4 h-4 flex-shrink-0 rounded-full bg-gray-200 dark:bg-[#2D3148] text-gray-600 dark:text-[#94A3B8] text-[10px] font-bold flex items-center justify-center mt-0.5">{j + 1}</span>
                          <span className="text-gray-500 dark:text-[#94A3B8] text-sm">{fix}</span>
                        </li>
                      ))}
                    </ol>
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-white/50 dark:bg-[#0A0E1A]/50 border border-gray-200/50 dark:border-[#2D3148]/50">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 text-gray-400 dark:text-[#64748B]">
                        <i className="ri-information-line text-sm"></i>
                      </div>
                      <span className="text-gray-500 dark:text-[#64748B] text-xs leading-relaxed">{item.note}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Section 8: Security ── */}
              <SectionHeading id="security">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 flex-shrink-0">
                  <i className="ri-shield-check-line text-lg"></i>
                </span>
                Security Best Practices
              </SectionHeading>

              <Prose>
                Your API key is essentially a password. If someone gets hold of it, they can make requests on your behalf — burning through your free quota or, if you've added billing, charging your account. Follow these practices.
              </Prose>

              <Callout type="danger" title="Never share your API key publicly">
                Don't paste it in Discord servers, GitHub repos, social media, or email. Anyone who has your key can use your daily quota — or rack up charges if you have billing enabled. JobOS stores your key locally in your browser and never sends it to our servers.
              </Callout>

              <Prose>
                Here's what good key hygiene looks like in practice:
              </Prose>

              <div className="relative my-6 rounded-xl overflow-hidden border border-gray-200 dark:border-[#2D3148]">
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-100 dark:bg-[#141828] border-b border-gray-200 dark:border-[#2D3148]">
                  <span className="text-xs font-mono font-semibold tracking-widest text-gray-400 dark:text-[#64748B] uppercase">Key Management Checklist</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                    <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
                  </div>
                </div>
                <pre className="p-5 bg-gray-50 dark:bg-[#0D1117] overflow-x-auto text-sm font-mono leading-7">
                  <code>
                    <span className="text-gray-400 dark:text-[#64748B]"># </span><span className="text-emerald-400">✅ DO these things</span>{'\n'}
                    <span className="text-[#818CF8]">Store</span>{'   → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Password manager (1Password, Bitwarden, etc.)</span>{'\n'}
                    <span className="text-[#818CF8]">Rotate</span>{'  → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Create a new key every 3–6 months</span>{'\n'}
                    <span className="text-[#818CF8]">Monitor</span>{'  → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Check usage in AI Studio Dashboard monthly</span>{'\n'}
                    <span className="text-[#818CF8]">Restrict</span>{' → '}<span className="text-gray-700 dark:text-[#E2E8F0]">In Google Cloud Console, limit to Generative API only</span>{'\n'}
                    {'\n'}
                    <span className="text-gray-400 dark:text-[#64748B]"># </span><span className="text-red-400">❌ NEVER do these things</span>{'\n'}
                    <span className="text-red-400">Share</span>{'   → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Discord, Slack, email, screenshots, screen shares</span>{'\n'}
                    <span className="text-red-400">Commit</span>{'  → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Git repos, .env files pushed to GitHub</span>{'\n'}
                    <span className="text-red-400">Reuse</span>{'   → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Same key across multiple untrusted tools</span>{'\n'}
                    <span className="text-red-400">Ignore</span>{'  → '}<span className="text-gray-700 dark:text-[#E2E8F0]">Unexpected usage spikes in your dashboard</span>
                  </code>
                </pre>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <div className="p-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-5 flex items-center justify-center text-emerald-400">
                      <i className="ri-check-double-line text-base"></i>
                    </div>
                    <span className="text-emerald-400 font-bold text-sm uppercase tracking-wide">Do</span>
                  </div>
                  <div className="space-y-1">
                    <CheckItem isGood={true}>Store your key only in trusted apps like JobOS</CheckItem>
                    <CheckItem isGood={true}>Use a separate key for each application you connect</CheckItem>
                    <CheckItem isGood={true}>Set API key restrictions in Google Cloud Console</CheckItem>
                    <CheckItem isGood={true}>Rotate your key every 90 days as a habit</CheckItem>
                    <CheckItem isGood={true}>Monitor usage in Google AI Studio dashboard</CheckItem>
                    <CheckItem isGood={true}>Revoke immediately if you suspect compromise</CheckItem>
                  </div>
                </div>
                <div className="p-5 rounded-xl border border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-5 flex items-center justify-center text-red-400">
                      <i className="ri-close-circle-line text-base"></i>
                    </div>
                    <span className="text-red-400 font-bold text-sm uppercase tracking-wide">Don't</span>
                  </div>
                  <div className="space-y-1">
                    <CheckItem isGood={false}>Share your key in screenshots or screen recordings</CheckItem>
                    <CheckItem isGood={false}>Paste your key into chat apps, emails, or forums</CheckItem>
                    <CheckItem isGood={false}>Commit your key to any Git repository</CheckItem>
                    <CheckItem isGood={false}>Use the same key across multiple untrusted tools</CheckItem>
                    <CheckItem isGood={false}>Leave unused keys active — delete them</CheckItem>
                    <CheckItem isGood={false}>Add billing before you understand your usage patterns</CheckItem>
                  </div>
                </div>
              </div>

              <SubHeading>Setting API Key Restrictions (Recommended)</SubHeading>
              <Prose>
                Google lets you restrict your API key to only work with specific APIs. This limits the blast radius if your key is ever compromised:
              </Prose>

              <CodeBlock
                language="steps"
                code={`1. Go to console.cloud.google.com/apis/credentials
2. Click on your API key name
3. Under "API restrictions", select "Restrict key"
4. Choose "Generative Language API" from the dropdown
5. Click Save

Your key will now ONLY work for Gemini — even if stolen,
it can't be used to access other Google services.`}
              />

              <Callout type="info" title="JobOS never asks for more than your API key">
                We will never ask for your Google account password, OAuth tokens, or any other credentials. If any tool asks for your Google password to "connect" to Gemini, that's a red flag — don't proceed.
              </Callout>

              {/* ── Section 9: When to Upgrade ── */}
              <SectionHeading id="upgrade">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#6366F1]/10 text-[#818CF8] flex-shrink-0">
                  <i className="ri-arrow-up-circle-line text-lg"></i>
                </span>
                When and How to Upgrade
              </SectionHeading>

              <Prose>
                The free tier is genuinely enough for most job seekers. But here's an honest breakdown breakdown when it makes sense to upgrade — and what it actually costs.
              </Prose>

              <SubHeading>Signs You've Outgrown the Free Tier</SubHeading>
              <BulletList items={[
                'You\'re hitting the 1,500 RPD limit regularly (unlikely for personal use)',
                'You need guaranteed data privacy (paid tier explicitly excludes training)',
                'You want to use Gemini 1.5 Pro heavily (only 50 RPD free)',
                'You\'re building a tool or automation that runs overnight',
                'You need higher throughput for batch processing multiple resumes',
              ]} />

              <SubHeading>Pay-As-You-Go Pricing (March 2026)</SubHeading>
              <Prose>
                Google's paid tier is consumption-based — you only pay for what you use. There's no monthly subscription.
              </Prose>

              <div className="overflow-x-auto mb-6 rounded-xl border border-gray-200 dark:border-[#2D3148]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#0D1117] border-b border-gray-200 dark:border-[#2D3148]">
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Model</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Input (per 1M tokens)</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Output (per 1M tokens)</th>
                      <th className="text-left px-5 py-4 text-gray-500 dark:text-[#64748B] font-semibold text-xs uppercase tracking-wider">Est. cost / resume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#2D3148] bg-white dark:bg-[#0A0E1A]">
                    {[
                      { model: 'Gemini 2.0 Flash', input: '$0.075', output: '$0.30', perResume: '~$0.001' },
                      { model: 'Gemini 2.0 Flash-Lite', input: '$0.0375', output: '$0.15', perResume: '~$0.0005' },
                      { model: 'Gemini 1.5 Pro', input: '$1.25', output: '$5.00', perResume: '~$0.01' },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#0D1117] transition-colors">
                        <td className="px-5 py-3.5 text-gray-900 dark:text-white font-medium text-sm">{row.model}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] font-mono text-sm">{row.input}</td>
                        <td className="px-5 py-3.5 text-gray-600 dark:text-[#94A3B8] font-mono text-sm">{row.output}</td>
                        <td className="px-5 py-3.5 text-emerald-400 font-semibold text-sm">{row.perResume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Callout type="tip" title="Real cost context">
                At $0.001 per resume tailoring, you'd need to process <strong>1,000 resumes</strong> to spend $1. Even the most aggressive job seekers applying to 50 roles/week would spend less than $0.05/month on the paid tier. The free tier is almost certainly enough — but if you do upgrade, it's essentially free anyway.
              </Callout>

              <SubHeading>How to Enable Billing</SubHeading>
              <BulletList items={[
                'Go to console.cloud.google.com/billing',
                'Click "Link a billing account" for your project',
                'Add a credit card (you won\'t be charged until you exceed free limits)',
                'Set a budget alert at $5/month so you\'re never surprised',
                'The free tier limits still apply — billing only kicks in above them',
              ]} />

              <Callout type="warning" title="Set a budget alert before enabling billing">
                Always set a budget alert before adding billing. Go to console.cloud.google.com/billing → Budgets &amp; alerts → Create budget. Set it to $5 or $10. You'll get an email if you're approaching that threshold — giving you time to investigate before any real charges occur.
              </Callout>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-14 pt-8 border-t border-gray-200 dark:border-[#2D3148]">
                {['Google AI Studio', 'Gemini API', 'Free API Key', 'AI Tools', 'Job Search', 'JobOS', 'Tutorial'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-gray-100 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] rounded-full text-gray-500 dark:text-[#94A3B8] text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-4 mt-6">
                <span className="text-gray-500 dark:text-[#94A3B8] text-sm font-medium">Share:</span>
                <a
                  href="https://twitter.com/intent/tweet?text=Get%20a%20Free%20Gemini%20API%20Key%20in%20Under%202%20Minutes%20%E2%80%94%20Full%20Tutorial&url=https://jobos.dev/blog/google-ai-studio-free-api-key"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] text-gray-500 dark:text-[#94A3B8] hover:text-[#6366F1] hover:border-[#6366F1]/50 transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-x-line text-sm"></i>
                </a>
                <div className="flex items-center gap-1.5">
                  <a
                    href="https://www.linkedin.com/sharing/share-offsite/?url=https://jobos.dev/blog/google-ai-studio-free-api-key"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] text-gray-500 dark:text-[#94A3B8] hover:text-[#0077B5] hover:border-[#0077B5]/50 transition-colors cursor-pointer"
                  >
                    <i className="ri-linkedin-fill text-sm"></i>
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('Get a Free Gemini API Key in Under 2 Minutes — No credit card, no subscription. Just your Google account. Full tutorial: https://jobos.dev/blog/google-ai-studio-free-api-key');
                      setLinkedinMsgCopied(true);
                      setTimeout(() => setLinkedinMsgCopied(false), 2500);
                    }}
                    className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${linkedinMsgCopied ? 'bg-[#0077B5] border-[#0077B5] text-white' : 'bg-gray-100 dark:bg-[#141828] border-gray-200 dark:border-[#2D3148] text-gray-500 dark:text-[#94A3B8] hover:border-[#0077B5] hover:text-[#0077B5]'}`}
                    title="Copy message to paste into LinkedIn"
                  >
                    <i className={linkedinMsgCopied ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                    {linkedinMsgCopied ? 'Copied!' : 'Copy message'}
                  </button>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(window.location.href)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] text-gray-500 dark:text-[#94A3B8] hover:text-[#6366F1] hover:border-[#6366F1]/50 transition-colors cursor-pointer"
                  title="Copy link"
                >
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="pb-16 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="rounded-2xl bg-gradient-to-br from-[#6366F1]/10 to-[#818CF8]/5 border border-[#6366F1]/20 p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <div className="text-[#818CF8] text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <span>✦</span> Put this into practice
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                You have the key — now use it
              </h3>
              <p className="text-gray-500 dark:text-[#94A3B8] text-sm leading-relaxed">
                Paste your Gemini API key into JobOS and instantly unlock AI resume tailoring, cover letter generation, ATS scoring, and autofill — all free.
              </p>
            </div>
            <a
              href="https://app.jobos.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 h-12 px-8 bg-[#6366F1] text-white text-sm font-semibold rounded-full hover:bg-[#4F46E5] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#6366F1]/30 flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Open JobOS Free
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Continue Learning — Next 2 Tutorials */}
      <section className="py-16 bg-gray-50 dark:bg-[#060A14] border-t border-gray-200 dark:border-[#2D3148]">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[#818CF8] text-xs font-semibold uppercase tracking-wider mb-2 flex items-center justify-center gap-2">
              <span>✦</span> Tutorial Series
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Continue Learning</h2>
            <p className="text-gray-500 dark:text-[#94A3B8] text-sm mt-2">Two more free AI API options — each with different strengths</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/groq-free-api-key"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-flashlight-fill text-orange-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Groq API</h4>
              <p className="text-sm text-gray-600 mb-3">Lightning-fast inference speeds</p>
              <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                Read tutorial
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </span>
            </Link>

            <Link
              to="/blog/grok-free-api-key"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-twitter-x-fill text-purple-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Grok (xAI)</h4>
              <p className="text-sm text-gray-600 mb-3">Real-time knowledge with X integration</p>
              <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                Read tutorial
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </span>
            </Link>

            <Link
              to="/blog/openrouter-free-api-key"
              className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-route-line text-amber-600 text-2xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">OpenRouter</h4>
              <p className="text-sm text-gray-600 mb-3">Access 100+ AI models via one API</p>
              <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                Read tutorial
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </span>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 h-11 px-8 border border-gray-200 dark:border-[#2D3148] text-gray-700 dark:text-[#94A3B8] text-sm font-medium rounded-full hover:border-[#6366F1] hover:text-[#6366F1] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to All Articles
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
